/**
 * Modelo de campo del PDFFormBuilder (Fase 1 del SPEC).
 *
 * Decisiones de diseño fijadas para F3-2683:
 *  - Las coordenadas se almacenan en PUNTOS PDF con origen abajo-izquierda
 *    (convención PDF nativa). El eje Y crece hacia arriba. El frontend hace
 *    el flip de Y al pintar (ver PdfCoordinateService).
 *  - El tipo de campo es SEMÁNTICO (TEXT, RUT, ...), no una referencia a
 *    componente como en el stack de firmas. La resolución tipo -> componente
 *    es responsabilidad de la capa de UI.
 *  - SIGNATURE / INITIALS son un tipo de campo más, con `assignedTo` para el
 *    firmante, de modo que converjan con el flujo de firma existente.
 *
 * El contrato NO debe asumir que estos son los únicos tipos posibles: nuevos
 * tipos (y en el futuro, detección por IA) deben poder sumarse sin reescribir
 * el modelo.
 */

import { v4 as uuidv4 } from 'uuid';
import { isValidRut } from '@/febos/global/pdf/formBuilder/utils/rutUtils';

/**
 * Tipos de campo semánticos de la primera versión.
 * @enum {string}
 */
export const FIELD_TYPES = Object.freeze({
  TEXT: 'TEXT',
  TEXTAREA: 'TEXTAREA',
  RUT: 'RUT',
  EMAIL: 'EMAIL',
  DATE: 'DATE',
  NUMBER: 'NUMBER',
  SELECT: 'SELECT',
  CHECKBOX: 'CHECKBOX',
  //SIGNATURE: 'SIGNATURE',
  //INITIALS: 'INITIALS',
});

/**
 * Origen de un campo: creado manualmente o derivado de una detección.
 * @enum {string}
 */
export const FIELD_SOURCE = Object.freeze({
  MANUAL: 'MANUAL',
  AUTO: 'AUTO',
});

/**
 * Alineaciones de texto soportadas.
 * @enum {string}
 */
export const TEXT_ALIGNMENT = Object.freeze({
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
});

/**
 * Tipos que representan una zona de firma (no se renderizan como texto en el
 * PDF final; los consume el módulo de firma). Útil para que la UI y el motor
 * de generación los traten distinto.
 *
 * Se mantienen en el modelo porque el flujo de firma existente los usará
 * (SPEC §44-45, con assignedTo), aunque el form builder de datos NO los ofrece
 * en su paleta por defecto.
 */
export const SIGNATURE_FIELD_TYPES = Object.freeze([
  FIELD_TYPES.SIGNATURE,
  FIELD_TYPES.INITIALS,
]);

/**
 * Tipos de campo de DATOS (los que ofrece el form builder por defecto). Excluye
 * las zonas de firma, que las administra el módulo de firma, no este editor.
 */
export const DATA_FIELD_TYPES = Object.freeze([
  FIELD_TYPES.TEXT,
  FIELD_TYPES.TEXTAREA,
  FIELD_TYPES.RUT,
  FIELD_TYPES.EMAIL,
  FIELD_TYPES.DATE,
  FIELD_TYPES.NUMBER,
  FIELD_TYPES.SELECT,
  FIELD_TYPES.CHECKBOX,
]);

/**
 * @typedef {Object} FieldStyle
 * @property {string} fontFamily
 * @property {number} fontSize
 * @property {('left'|'center'|'right')} alignment
 */

/**
 * @typedef {Object} FieldDetection
 * @property {('MANUAL'|'AUTO')} source
 * @property {number} [confidence] 0..1 (solo detecciones automáticas)
 * @property {string} [reason]     explicación legible de la detección
 */

/**
 * @typedef {Object} PdfField
 * @property {string}  id       UUID estable (nunca derivado del label).
 * @property {string}  type     uno de FIELD_TYPES.
 * @property {string}  name     nombre interno único (snake_case).
 * @property {string}  label    etiqueta legible para el usuario.
 * @property {number}  page     1-based.
 * @property {number}  x        punto PDF, origen abajo-izquierda.
 * @property {number}  y        punto PDF, origen abajo-izquierda.
 * @property {number}  width    puntos PDF.
 * @property {number}  height   puntos PDF.
 * @property {boolean} required
 * @property {*}       defaultValue
 * @property {string}  placeholder
 * @property {FieldStyle}     style
 * @property {FieldDetection} detection
 * @property {?string} assignedTo  participante asignado (solo SIGNATURE/INITIALS).
 */

const DEFAULT_FIELD_SIZE = Object.freeze({
  // Tamaños por defecto expresados en puntos PDF (1 pt = 1/72").
  [FIELD_TYPES.TEXT]: { width: 180, height: 18 },
  [FIELD_TYPES.TEXTAREA]: { width: 220, height: 60 },
  [FIELD_TYPES.RUT]: { width: 120, height: 18 },
  [FIELD_TYPES.EMAIL]: { width: 200, height: 18 },
  [FIELD_TYPES.DATE]: { width: 90, height: 18 },
  [FIELD_TYPES.NUMBER]: { width: 90, height: 18 },
  [FIELD_TYPES.SELECT]: { width: 160, height: 18 },
  [FIELD_TYPES.CHECKBOX]: { width: 14, height: 14 },
  [FIELD_TYPES.SIGNATURE]: { width: 160, height: 60 },
  [FIELD_TYPES.INITIALS]: { width: 60, height: 40 },
});

const DEFAULT_STYLE = Object.freeze({
  fontFamily: 'Helvetica',
  fontSize: 10,
  alignment: TEXT_ALIGNMENT.LEFT,
});

/**
 * Tamaño por defecto (en puntos PDF) para un tipo dado.
 * @param {string} type
 * @returns {{width:number, height:number}}
 */
export function defaultSizeForType(type) {
  return { ...(DEFAULT_FIELD_SIZE[type] || DEFAULT_FIELD_SIZE[FIELD_TYPES.TEXT]) };
}

/**
 * ¿El tipo es una zona de firma?
 * @param {string} type
 * @returns {boolean}
 */
export function isSignatureField(type) {
  return SIGNATURE_FIELD_TYPES.includes(type);
}

/**
 * Normaliza un texto a un nombre interno válido (snake_case ASCII).
 * @param {string} raw
 * @returns {string}
 */
export function slugifyFieldName(raw) {
  const base = String(raw || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // quita acentos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return base || 'campo';
}

/**
 * Genera un nombre interno único dentro de una colección de campos existente.
 * Ej: nombre, nombre_2, nombre_3.
 * @param {string} base          nombre base (ya slugificado o no)
 * @param {PdfField[]} existing  campos existentes
 * @returns {string}
 */
export function uniqueFieldName(base, existing = []) {
  const slug = slugifyFieldName(base);
  const taken = new Set(existing.map((f) => f.name));
  if (!taken.has(slug)) {
    return slug;
  }
  let i = 2;
  while (taken.has(`${slug}_${i}`)) {
    i += 1;
  }
  return `${slug}_${i}`;
}

/**
 * Crea un campo con valores por defecto coherentes con su tipo.
 * No posiciona el campo; x/y/width/height deben venir del editor en puntos PDF.
 *
 * @param {Object} params
 * @param {string} params.type
 * @param {number} params.page
 * @param {number} params.x
 * @param {number} params.y
 * @param {number} [params.width]
 * @param {number} [params.height]
 * @param {string} [params.label]
 * @param {string} [params.name]
 * @param {PdfField[]} [params.existing] para unicidad de `name`
 * @param {FieldDetection} [params.detection]
 * @returns {PdfField}
 */
export function createField({
  type = FIELD_TYPES.TEXT,
  page = 1,
  x = 0,
  y = 0,
  width,
  height,
  label,
  name,
  existing = [],
  detection,
} = {}) {
  const size = defaultSizeForType(type);
  const resolvedLabel = label || defaultLabelForType(type);
  const resolvedName = uniqueFieldName(name || resolvedLabel, existing);

  const field = {
    id: uuidv4(),
    type,
    name: resolvedName,
    label: resolvedLabel,
    page,
    x,
    y,
    width: width != null ? width : size.width,
    height: height != null ? height : size.height,
    required: false,
    defaultValue: null,
    placeholder: '',
    // Clave de dato opcional: campos con la MISMA linkKey comparten el valor
    // (se sincronizan al completar). Vacío = campo independiente (usa su id).
    linkKey: '',
    style: { ...DEFAULT_STYLE },
    detection: detection || { source: FIELD_SOURCE.MANUAL },
  };

  if (isSignatureField(type)) {
    field.assignedTo = null;
  }

  // Los campos de lista llevan sus opciones (lista simple de strings).
  if (type === FIELD_TYPES.SELECT) {
    field.options = [];
  }

  // Los campos de fecha llevan el formato de salida (cómo se estampa/muestra).
  if (type === FIELD_TYPES.DATE) {
    field.dateFormat = DEFAULT_DATE_FORMAT;
  }

  return field;
}

/**
 * Formatos de fecha disponibles para campos DATE. El valor se almacena en ISO
 * (yyyy-mm-dd, formato nativo del input date) y se FORMATEA a este patrón al
 * estamparlo/mostrarlo.
 */
export const DATE_FORMATS = Object.freeze([
  { value: 'dd-mm-yyyy', label: 'dd-mm-aaaa (31-12-2026)' },
  { value: 'dd/mm/yyyy', label: 'dd/mm/aaaa (31/12/2026)' },
  { value: 'yyyy-mm-dd', label: 'aaaa-mm-dd (2026-12-31)' },
  { value: 'dd-mm-yy', label: 'dd-mm-aa (31-12-26)' },
]);

export const DEFAULT_DATE_FORMAT = 'dd-mm-yyyy';

/**
 * Formatea una fecha ISO (yyyy-mm-dd) según el patrón indicado. Si el valor no
 * es una fecha ISO válida, lo devuelve tal cual.
 * @param {string} isoValue  fecha en formato yyyy-mm-dd
 * @param {string} fmt       uno de DATE_FORMATS[].value
 * @returns {string}
 */
export function formatDateValue(isoValue, fmt) {
  const s = String(isoValue || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    return s;
  }
  const [, yyyy, mm, dd] = m;
  const yy = yyyy.slice(2);
  switch (fmt) {
    case 'dd/mm/yyyy':
      return `${dd}/${mm}/${yyyy}`;
    case 'yyyy-mm-dd':
      return `${yyyy}-${mm}-${dd}`;
    case 'dd-mm-yy':
      return `${dd}-${mm}-${yy}`;
    case 'dd-mm-yyyy':
    default:
      return `${dd}-${mm}-${yyyy}`;
  }
}

const DEFAULT_LABELS = Object.freeze({
  [FIELD_TYPES.TEXT]: 'Texto',
  [FIELD_TYPES.TEXTAREA]: 'Texto largo',
  [FIELD_TYPES.RUT]: 'RUT',
  [FIELD_TYPES.EMAIL]: 'Correo',
  [FIELD_TYPES.DATE]: 'Fecha',
  [FIELD_TYPES.NUMBER]: 'Número',
  [FIELD_TYPES.SELECT]: 'Lista',
  [FIELD_TYPES.CHECKBOX]: 'Checkbox',
  [FIELD_TYPES.SIGNATURE]: 'Firma',
  [FIELD_TYPES.INITIALS]: 'Iniciales',
});

/**
 * Etiqueta por defecto para un tipo.
 * @param {string} type
 * @returns {string}
 */
export function defaultLabelForType(type) {
  return DEFAULT_LABELS[type] || 'Campo';
}

const SAMPLE_VALUES = Object.freeze({
  [FIELD_TYPES.TEXT]: 'Juan Pérez',
  [FIELD_TYPES.TEXTAREA]: 'Texto de varias líneas...',
  [FIELD_TYPES.RUT]: '12.345.678-5',
  [FIELD_TYPES.EMAIL]: 'correo@ejemplo.cl',
  [FIELD_TYPES.DATE]: '31-12-2026',
  [FIELD_TYPES.NUMBER]: '1234',
  [FIELD_TYPES.SELECT]: 'Opción',
  [FIELD_TYPES.CHECKBOX]: '',
  [FIELD_TYPES.SIGNATURE]: 'Firma',
  [FIELD_TYPES.INITIALS]: 'JP',
});

/**
 * Valor de ejemplo para previsualizar un campo según su tipo.
 * @param {string} type
 * @returns {string}
 */
export function sampleValueForType(type) {
  return SAMPLE_VALUES[type] != null ? SAMPLE_VALUES[type] : '';
}

/**
 * Clave con la que se indexa el VALOR de un campo. Si el campo define una
 * `linkKey`, todos los campos con esa misma clave comparten el valor (se
 * sincronizan al completar). Si no, cada campo usa su id (independiente).
 * @param {PdfField} field
 * @returns {string}
 */
export function valueKeyOf(field) {
  const key = field && typeof field.linkKey === 'string' ? field.linkKey.trim() : '';
  return key || (field && field.id);
}

/**
 * ¿El valor está vacío para efectos de "requerido"?
 * @param {string} type
 * @param {*} value
 * @returns {boolean}
 */
export function isEmptyValue(type, value) {
  if (type === FIELD_TYPES.CHECKBOX) {
    return !value;
  }
  return value == null || String(value).trim() === '';
}

/**
 * Valida el valor de un campo según su tipo y reglas (required, formato).
 * Extensible: nuevos tipos añaden su rama sin reescribir el resto.
 *
 * @param {PdfField} field
 * @param {*} value
 * @returns {{ valid: boolean, error: ?string }}
 */
export function validateFieldValue(field, value) {
  const empty = isEmptyValue(field.type, value);

  // Requerido.
  if (field.required && empty) {
    return { valid: false, error: 'Campo requerido' };
  }
  // Vacío y no requerido: válido (no se valida formato de un valor ausente).
  if (empty) {
    return { valid: true, error: null };
  }

  // Validación de formato por tipo.
  switch (field.type) {
    case FIELD_TYPES.RUT:
      return isValidRut(String(value))
        ? { valid: true, error: null }
        : { valid: false, error: 'RUT inválido' };
    case FIELD_TYPES.EMAIL:
      return isValidEmail(String(value))
        ? { valid: true, error: null }
        : { valid: false, error: 'Correo inválido' };
    case FIELD_TYPES.SELECT: {
      const options = Array.isArray(field.options) ? field.options : [];
      const values = options.map((o) => String(o && o.value != null ? o.value : o));
      return values.includes(String(value))
        ? { valid: true, error: null }
        : { valid: false, error: 'Opción no válida' };
    }
    default:
      return { valid: true, error: null };
  }
}

// Validación simple de email: una arroba, con texto antes y un dominio con punto.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * ¿El valor es un email con formato válido?
 * @param {string} value
 * @returns {boolean}
 */
export function isValidEmail(value) {
  return EMAIL_REGEX.test(String(value || '').trim());
}
